import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import GetCookie from "../../../../utils/GetCookie";
import ErrorPopup from "../../../../utils/popup/ErrorPopup";
import ProcessingPopup from "../../../../utils/popup/ProcessingPopup";
import SuccessPopup from "../../../../utils/popup/SuccessPopup";
import PaginationControls from "../../../../utils/PaginationControls";
import authNavigate from "../../../../globalComponents/AuthNav";
import { useNavigate } from "react-router-dom";
import { buildTagOptionsFromPicoJson } from "../../../../utils/Picocolors";
import ConfirmationModal from "../../../../utils/ConfirmationModal";
import TagFilterPanel from "./TagFilterPanel";
import DocumentPreview from "../../../../utils/DocumentPreview";
import JSZip from "jszip";
import YearRangePicker from "../../../../utils/YearRangePicker";


/* A native <select> renders its list through the OS and ignores the app's
   styling, so this is a toggle button plus a floating card of options: light
   rows, the selected one filled blue, at most 20vh tall and scrollable.
   Closes on an outside click or Esc. Same as AbstractsList. */
const InlineSelect = ({ id, openId, setOpenId, value, options = [], placeholder = "Select", width, className = "" }) => {
  const [localOpen, setLocalOpen] = useState(false);
  const rootRef = useRef(null);
  const listRef = useRef(null);
  const controlled = typeof setOpenId === "function";
  const isOpen = controlled ? openId === id : localOpen;
  const setOpen = (next) => {
    if (controlled) setOpenId(next ? id : null);
    else setLocalOpen(next);
  };
  const selected = options.find((o) => o.value === value);

  useEffect(() => {
    if (!isOpen) return undefined;

    // Bring the selected option into view inside the list (without
    // scrolling the filter panel itself).
    const list = listRef.current;
    const active = list?.querySelector('[data-selected="true"]');
    if (list && active) {
      list.scrollTop = active.offsetTop - list.clientHeight / 2 + active.offsetHeight / 2;
    }

    const onMouseDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return (
    <div ref={rootRef} className={`position-relative ${className}`} style={width ? { width } : undefined}>
      <button
        type="button"
        className="ev-btn-light-fill ev-btn-small ev-radius-xsmall w-100 d-flex align-items-center justify-content-between"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={() => setOpen(!isOpen)}
      >
        <span className="text-truncate">{selected ? selected.label : placeholder}</span>
        <i className={`bi bi-chevron-${isOpen ? "up" : "down"} ms-1`}></i>
      </button>
      {isOpen && (
        <div
          ref={listRef}
          role="listbox"
          className="position-absolute start-0 mt-1 p-2 bg-white border border-primary ev-radius-xsmall shadow-sm d-flex flex-column gap-1 scrollable-element"
          style={{
            top: "100%",
            zIndex: 1000,
            minWidth: "100%",
            width: "max-content",
            maxHeight: "20vh",
            overflowY: "auto",
          }}
        >
          {options.map((opt) => {
            const isSelected = value === opt.value;
            return (
              <button
                key={String(opt.value)}
                type="button"
                role="option"
                aria-selected={isSelected}
                data-selected={isSelected ? "true" : undefined}
                className={`${isSelected ? "ev-btn-blue-fill-round" : "ev-btn-light-fill"} ev-btn-small ev-radius-xsmall w-100 text-start text-nowrap flex-shrink-0`}
                onClick={() => { opt.onSelect?.(); setOpen(false); }}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const ADV_FIELD_OPTIONS = [
  { value: "", label: "Field" },
  { value: "title", label: "Title" },
  { value: "abstract", label: "Abstract" },
  { value: "author", label: "Author" },
  { value: "year", label: "Year" },
  { value: "population", label: "Population" },
  { value: "intervention", label: "Intervention" },
  { value: "comparison", label: "Comparator" },
  { value: "outcome", label: "Outcome" },
  { value: "study_design", label: "Study Design" },
  { value: "exclusion", label: "Exclusion" },
];

const ADV_OPERATOR_OPTIONS = [
  { value: "contains", label: "Contains" },
  { value: "not_contains", label: "Does not contain" },
  { value: "equals", label: "Equals" },
  { value: "not_equals", label: "Does not equal" },
  { value: "begins_with", label: "Starts with" },
  { value: "not_begins_with", label: "Does not start with" },
  { value: "ends_with", label: "Ends with" },
  { value: "not_ends_with", label: "Does not end with" },
  { value: "is_empty", label: "Is empty" },
  { value: "is_not_empty", label: "Is not empty" },
  { value: "is_null", label: "Is null" },
  { value: "is_not_null", label: "Is not null" },
];

/* ─────────────────────────────────────────────
   Helpers
───────────────────────────────────────────── */
const formatGroupLabel = (groupId) => `Study_${groupId}`;
const getSid = (s) => s?.study_id ?? s?.id;

const getDefaultGroupName = (primary) => {
    const authors = primary?.authors;
    let firstName = "";
    if (Array.isArray(authors) && authors.length > 0) {
        firstName = String(authors[0]);
    } else if (typeof authors === "string" && authors) {
        firstName = authors.split(";")[0].split(",")[0];
    } else if (primary?.author_names) {
        firstName = String(primary.author_names).split(";")[0].split(",")[0];
    }
    const parts = firstName.trim().split(/\s+/);
    const lastName = parts[parts.length - 1] || "Unknown";
    const year = primary?.publication_year || primary?.year || "";
    return year ? `${lastName}_${year}` : lastName;
};

const resolveGroupName = (baseName, existingGroups) => {
    const names = new Set(existingGroups.map((g) => g.name));
    if (!names.has(baseName)) return baseName;
    for (const letter of "abcdefghijklmnopqrstuvwxyz") {
        const candidate = `${baseName}_${letter}`;
        if (!names.has(candidate)) return candidate;
    }
    return `${baseName}_${Date.now()}`;
};

const formatAuthors = (study) => {
    const a = study?.author_names || study?.authors;
    if (!a) return null;
    const str = Array.isArray(a) ? a.join("; ") : String(a);
    return str.trim() ? `Authors. ${str.trim()}` : null;
};

/* "Bibliographic" = paper added via Full-Text QC → Add Paper.
   Ids come from /qc/bibliographic-study-ids via context, so every card
   (Included, Primary, Linked) can show the badge without prop drilling. */
const BibliographicIdsContext = React.createContext(new Set());
const BibliographicBadge = ({ study }) => {
    const ids = React.useContext(BibliographicIdsContext);
    if (!ids.has(String(getSid(study) ?? ""))) return null;
    return (
        <span className="ev-message-card3 ev-radius-xsmall ev-btn-xsmall text-nowrap">
            <i className="bi bi-journal-text pe-1"></i>Bibliographic
        </span>
    );
};

const PaperIdBadge = ({ study, className = "ev-message-card5 ev-radius-xsmall ev-btn-xsmall" }) => {
    const sid = getSid(study);
    return <span className={className}>ID: {sid}</span>;
};

/* ─────────────────────────────────────────────
   DecisionBadge
───────────────────────────────────────────── */
const DecisionBadge = ({ decision }) => {
    const norm = String(decision || "").toLowerCase();
    const ok = norm === "included";
    if (!decision) return null;
    return (
        <span className={`ev-decision-card ${ok ? "included" : "excluded"}`}>
            <span className={`ev-decision-icon ${ok ? "included" : "excluded"}`}>
                <i className={`bi ${ok ? "bi-check-lg" : "bi-x-lg"}`} />
            </span>
            {ok ? "Include" : "Exclude"}
        </span>
    );
};

/* ─────────────────────────────────────────────
   PrimaryTag  (gradient pill, mirrors DecisionBadge's layout/size)
───────────────────────────────────────────── */
const PrimaryTag = () => (
    <span
        className="d-inline-flex align-items-center gap-1 ev-btn-xsmall ev-bold-med text-white"
        style={{
            borderRadius: 999,
            padding: "2px 10px 2px 2px",
            background: "linear-gradient(90deg, #4F46E5 0%, #6D5EF1 55%, #7C6CF6 100%)",
        }}
    >
        <span
            className="d-inline-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
            style={{ width: 18, height: 18, background: "rgba(255,255,255,0.28)" }}
        >
            <i className="bi bi-star-fill" style={{ fontSize: 9, color: "#fff" }} />
        </span>
        Primary
    </span>
);

/* ─────────────────────────────────────────────
   Group border color palette (cycles per group)
───────────────────────────────────────────── */
const GROUP_BORDER_COLORS = [
    "#A5B4FC", "#F1A8A4", "#A8DAB5", "#FFCC99", "#D3A6E0",
    "#99E0EA", "#F5A9C4", "#AEB8E8", "#F7B899", "#99D6C9",
];


/* ─────────────────────────────────────────────
   NetworkGraphModal
───────────────────────────────────────────── */
const NetworkGraphModal = ({ groups, onClose }) => {
    const COLS = Math.min(Math.max(groups.length, 1), 3);
    const CLUSTER_W = 380;
    const CLUSTER_H = 320;
    const ROWS = Math.ceil(groups.length / COLS);
    const PAD = 60;
    const PRIMARY_R = 52;
    const LINKED_R = 34;

    const clusters = groups.map((g, gi) => {
        const col = gi % COLS;
        const row = Math.floor(gi / COLS);
        const cx = PAD + col * CLUSTER_W + CLUSTER_W / 2;
        const cy = PAD + row * CLUSTER_H + CLUSTER_H / 2;
        const orbitR = Math.max(110, Math.min(g.linked.length * 22, 145));
        const allPapers = [g.primary, ...g.linked].filter(Boolean);
        const MAX_VIS = 8;
        const visible = allPapers.slice(0, MAX_VIS);
        const extra = allPapers.length - MAX_VIS;
        const linked = visible.map((s, li) => {
            const total = Math.max(visible.length, 1);
            const angle = (li / total) * 2 * Math.PI - Math.PI / 2;
            return { x: cx + orbitR * Math.cos(angle), y: cy + orbitR * Math.sin(angle), s, orbitR };
        });
        return { cx, cy, g, linked, extra, orbitR };
    });

    const svgW = COLS * CLUSTER_W + PAD * 2;
    const svgH = ROWS * CLUSTER_H + PAD * 2;

    return (
        <div
            className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
            style={{ zIndex: 2050, background: "rgba(0,0,0,0.45)" }}
        >
            <div
                className="ev-card-4 ev-radius-small p-4"
                style={{ width: "min(94vw, 1200px)", maxHeight: "90vh", overflowY: "auto" }}
            >
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <p className="ev-reg-p-dark ev-bold-med mb-0">
                        <i className="bi bi-diagram-3 me-2"></i>Grouped Papers — Network View
                    </p>
                    <button type="button" className="ev-btn-light-fill ev-btn-small" onClick={onClose}>
                        <i className="bi bi-x-lg"></i>
                    </button>
                </div>

                {groups.length === 0 ? (
                    <div className="text-center py-5">
                        <i className="bi bi-diagram-3 ev-fs-24 ev-small-p-blue mb-2 d-block"></i>
                        <p className="ev-small-p-dark mb-0">No groups created yet. Drag papers to the right panel to begin.</p>
                    </div>
                ) : (
                    <>
                        <div style={{ overflowX: "auto" }}>
                            <svg width={svgW} height={svgH} xmlns="http://www.w3.org/2000/svg" style={{ display: "block" }}>
                                {clusters.map(({ cx, cy, linked }, gi) =>
                                    linked.map(({ x, y }, li) => (
                                        <line key={`e-${gi}-${li}`} x1={cx} y1={cy} x2={x} y2={y}
                                            stroke="var(--color14)" strokeWidth="2" strokeDasharray="6 4" />
                                    ))
                                )}
                                {clusters.map(({ cx, cy, g, linked, extra, orbitR }, gi) => (
                                    <g key={gi}>
                                        <circle cx={cx} cy={cy} r={PRIMARY_R} fill="var(--color8)" stroke="var(--color9)" strokeWidth="3" />
                                        <text x={cx} y={cy + 5} textAnchor="middle" fontSize="11" fontWeight="700" fill="var(--color2)">
                                            {(g.name || "").length > 12 ? `${(g.name || "").slice(0, 12)}…` : (g.name || `Group_${getSid(g.primary)}`)}
                                        </text>
                                        {linked.map(({ x, y, s }, li) => (
                                            <g key={li}>
                                                <circle cx={x} cy={y} r={LINKED_R} fill="var(--color12)" stroke="var(--color14)" strokeWidth="2" />
                                                <text x={x} y={y + 5} textAnchor="middle" fontSize="10" fill="var(--color5)">ID:{getSid(s)}</text>
                                            </g>
                                        ))}
                                        {extra > 0 && (() => {
                                            const angle = (linked.length / Math.max(linked.length + 1, 1)) * 2 * Math.PI - Math.PI / 2;
                                            const x = cx + orbitR * Math.cos(angle);
                                            const y = cy + orbitR * Math.sin(angle);
                                            return (
                                                <g>
                                                    <line x1={cx} y1={cy} x2={x} y2={y} stroke="var(--color14)" strokeWidth="2" strokeDasharray="5 4" />
                                                    <circle cx={x} cy={y} r={LINKED_R} fill="var(--color10)" stroke="var(--color14)" strokeWidth="2" />
                                                    <text x={x} y={y + 5} textAnchor="middle" fontSize="11" fontWeight="600" fill="var(--color2)">+{extra}</text>
                                                </g>
                                            );
                                        })()}
                                    </g>
                                ))}
                            </svg>
                        </div>
                        <div className="d-flex gap-4 mt-3 justify-content-center">
                            <div className="d-flex align-items-center gap-2">
                                <div style={{ width: 20, height: 20, borderRadius: "50%", background: "var(--color8)", border: "3px solid var(--color9)", flexShrink: 0 }}></div>
                                <span className="ev-small-p-dark">Group Name</span>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                                <div style={{ width: 16, height: 16, borderRadius: "50%", background: "var(--color12)", border: "2px solid var(--color14)", flexShrink: 0 }}></div>
                                <span className="ev-small-p-dark">Paper ID</span>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};



/* ─────────────────────────────────────────────
   LinkPickerModal  (fixed overlay so it never gets
   clipped by an overflow-hidden ancestor)
───────────────────────────────────────────── */
const LinkPickerModal = ({ leftStudies, onLink, onClose }) => {
    const [pickerSearch, setPickerSearch]     = useState("");
    const [pickerSelected, setPickerSelected] = useState([]); // array of study objects

    const filteredLeft = useMemo(
        () => leftStudies.filter(
            (s) =>
                !pickerSearch ||
                s.title?.toLowerCase().includes(pickerSearch.toLowerCase()) ||
                String(getSid(s)).includes(pickerSearch),
        ),
        [leftStudies, pickerSearch],
    );

    const togglePickerItem = (s) => {
        const id = String(getSid(s));
        setPickerSelected((prev) =>
            prev.some((x) => String(getSid(x)) === id)
                ? prev.filter((x) => String(getSid(x)) !== id)
                : [...prev, s],
        );
    };
    const isPickerSelected = (s) => pickerSelected.some((x) => String(getSid(x)) === String(getSid(s)));

    return (
        <div
            className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
            style={{ zIndex: 2050, background: "rgba(0,0,0,0.45)" }}
            onClick={onClose}
        >
            <div
                className="ev-card-4 ev-radius-small p-3 d-flex flex-column"
                style={{ width: "min(92vw, 640px)", maxHeight: "80vh", overflowX: "hidden" }}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="d-flex justify-content-between align-items-center mb-2">
                    <p className="ev-reg-p-dark ev-bold-med mb-0">Link papers to this group</p>
                    <div className="d-flex align-items-center gap-2">
                        {pickerSelected.length > 0 && (
                            <span className="ev-message-card5 ev-radius-xsmall ev-btn-xsmall">
                                {pickerSelected.length} selected
                            </span>
                        )}
                        <button type="button" className="ev-btn-light-fill ev-btn-xsmall" onClick={onClose}>
                            <i className="bi bi-x-lg"></i>
                        </button>
                    </div>
                </div>

                <div className="position-relative w-100 mb-2">
                    <input
                        type="text"
                        className="form-control search-input ms-0"
                        placeholder="Search by title or ID…"
                        value={pickerSearch}
                        onChange={(e) => setPickerSearch(e.target.value)}
                        autoFocus
                    />
                    <button className="ev-btn-lightblue-fill-round search-bar-button">
                        <i className="bi bi-search"></i>
                    </button>
                </div>

                <div className="d-flex flex-column gap-1 scrollable-element pe-1" style={{ overflowY: "auto", flexGrow: 1, minHeight: 160 }}>
                    {filteredLeft.length === 0 ? (
                        <p className="ev-xsmall-p-dark text-center mb-0 py-2">
                            {leftStudies.length === 0 ? "All papers assigned" : "No match found"}
                        </p>
                    ) : (
                        filteredLeft.map((s) => {
                            const selected = isPickerSelected(s);
                            return (
                                <div
                                    key={getSid(s)}
                                    className={`p-2 ev-radius-xsmall cursor-pointer d-flex align-items-start gap-2 ${selected ? "ev-message-card1" : "ev-card-2"}`}
                                    onClick={() => togglePickerItem(s)}
                                >
                                    <input
                                        type="checkbox"
                                        className="flex-shrink-0 mt-1"
                                        checked={selected}
                                        onChange={() => togglePickerItem(s)}
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                    <div className="min-w-0 flex-grow-1" style={{ overflow: "hidden" }}>
                                        <div className="d-flex gap-1 align-items-center mb-1">
                                            <span className="ev-message-card5 ev-radius-xsmall ev-btn-xsmall">ID: {getSid(s)}</span>
                                            {s.publication_year && <span className="ev-xsmall-p-dark">{s.publication_year}</span>}
                                        </div>
                                        <p className="ev-xsmall-p-dark ev-bold-med mb-0 text-truncate">{s.title}</p>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>

                <div className="d-flex gap-1 mt-2">
                    <button
                        type="button"
                        className="ev-btn-light-fill ev-btn-small ev-radius-xsmall flex-grow-1"
                        onClick={onClose}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="ev-btn-blue-fill-round ev-btn-small flex-grow-1"
                        disabled={pickerSelected.length === 0}
                        onClick={() => {
                            pickerSelected.forEach((s) => onLink(s));
                            onClose();
                        }}
                    >
                        Link {pickerSelected.length > 0 ? `(${pickerSelected.length})` : ""}
                    </button>
                </div>
            </div>
        </div>
    );
};

/* ─────────────────────────────────────────────
   PrimaryGroupCard
───────────────────────────────────────────── */
const PrimaryGroupCard = ({
    group, groupIndex, leftStudies,
    onLink, onUnlink, onRemoveGroup, onOpenFullText, onMakePrimary, onRenameGroup,
}) => {
    const [showPicker, setShowPicker] = useState(false);
    const [expandedLinkedIds, setExpandedLinkedIds] = useState(() => new Set());
    const [isPrimaryAbstractOpen, setIsPrimaryAbstractOpen] = useState(false);
    const [showLinked, setShowLinked] = useState(false);
    const [isEditingName, setIsEditingName] = useState(false);
    const [nameInput, setNameInput] = useState(group.name || "");

    const borderColor = GROUP_BORDER_COLORS[groupIndex % GROUP_BORDER_COLORS.length];

    const toggleLinkedAbstract = (sid) => {
        setExpandedLinkedIds((prev) => {
            const next = new Set(prev);
            if (next.has(sid)) next.delete(sid); else next.add(sid);
            return next;
        });
    };

    const commitName = () => {
        const trimmed = nameInput.trim();
        if (trimmed) onRenameGroup(group.id, trimmed);
        else setNameInput(group.name || "");
        setIsEditingName(false);
    };

    return (
        <div
            className="slr-group-accent ev-card-8 ev-radius-small p-0 position-relative"
            style={{ borderLeftColor: borderColor }}
        >
            {/* ── Group Header ── */}
            <div className="d-flex justify-content-between align-items-center p-2 ev-card-2 ev-radius-small">
                <div className="d-flex gap-2 align-items-center flex-wrap">
                    {isEditingName ? (
                        <input
                            autoFocus
                            type="text"
                            className="formField4 ev-btn-small ev-radius-xsmall ev-bold-large"
                            style={{ maxWidth: 200 }}
                            value={nameInput}
                            onChange={(e) => setNameInput(e.target.value)}
                            onBlur={commitName}
                            onKeyDown={(e) => { if (e.key === "Enter") commitName(); if (e.key === "Escape") { setNameInput(group.name || ""); setIsEditingName(false); } }}
                        />
                    ) : (
                        <span
                            className="ev-small-p-dark ev-bold-large cursor-pointer d-flex align-items-center gap-1"
                            onClick={() => { setNameInput(group.name || ""); setIsEditingName(true); }}
                            title="Click to rename"
                        >
                            {group.name}
                            <i className="bi bi-pencil ev-fs-11 ev-xsmall-p-dark opacity-50"></i>
                        </span>
                    )}
                    {group.linked.length > 0 && (
                        <span className="ev-small-p-dark ev-bold-reg">{group.linked.length} linked</span>
                    )}

                </div>
                <div className="d-flex gap-1 align-items-center flex-shrink-0">
                    <button
                        type="button"
                        className="ev-btn-blue-outline-round ev-btn-xsmall"
                        onClick={() => setShowPicker(true)}
                        title="Link papers to this group"
                    >
                        <i className="bi bi-plus-lg me-1"></i>Link Papers
                    </button>
                    <button
                        type="button"
                        className="ev-btn-light-fill ev-btn-xsmall"
                        onClick={onRemoveGroup}
                        title="Remove group (papers return to left panel)"
                    >
                        <i className="bi bi-x-lg"></i>
                    </button>
                </div>
            </div>

            {/* ── Primary Paper Card ── */}
            <div className="p-3 py-2 mx-2 mt-2 ev-card-8 ev-radius-small">
                <div className="d-flex justify-content-between align-items-start mb-1">
                    <div className="d-flex gap-1 align-items-center flex-wrap">
                        <PaperIdBadge study={group.primary} className="ev-message-card5 ev-radius-xsmall ev-btn-xsmall" />
                        {(group.primary.publication_year || group.primary.year) && (
                            <span className="ev-message-card3 ev-radius-xsmall ev-btn-xsmall">Year {group.primary.publication_year || group.primary.year}</span>
                        )}
                        <BibliographicBadge study={group.primary} />
                        <PrimaryTag />
                    </div>
                </div>

                <p className="ev-bold-med ev-small-p-dark mt-2 mb-1">{group.primary.title}</p>
                <p className="ev-xsmall-p-dark mb-1">{formatAuthors(group.primary)}</p>

                <div className="d-flex justify-content-start align-items-center gap-2 mt-1">
                    <div
                        className="d-flex align-items-center gap-1 ev-small-p-blue ev-bold-med cursor-pointer"
                        onClick={() => setIsPrimaryAbstractOpen((p) => !p)}
                    >
                        <span>Abstract</span>
                        <i className={`bi ${isPrimaryAbstractOpen ? "bi-chevron-up" : "bi-chevron-down"} ev-fs-12`} />
                    </div>
                    <button
                        type="button"
                        className="ev-btn-lightblue-fill-round ev-btn-xsmall"
                        onClick={(e) => { e.stopPropagation(); onOpenFullText(group.primary); }}
                    >
                        <i className="bi bi-file-earmark-pdf me-1"></i>Full Text
                    </button>
                    <button
                        type="button"
                        className="ev-btn-blue-fill-round ev-btn-xsmall"
                        onClick={() => setShowPicker(true)}
                    >
                        <i className="bi bi-plus-lg me-1"></i>Link Papers
                    </button>
                    {group.linked.length > 0 && (
                        <button
                            type="button"
                            className="ev-btn-light-fill ev-btn-xsmall"
                            onClick={() => setShowLinked((p) => !p)}
                        >
                            <i className={`bi ${showLinked ? "bi-chevron-up" : "bi-chevron-down"} me-1`}></i>
                            {showLinked ? "Hide" : "View"} Linked ({group.linked.length})
                        </button>
                    )}
                </div>

                {isPrimaryAbstractOpen && (
                    <div className="mt-2 ev-card-8 p-2 ev-radius-xsmall">
                        <p className="ev-xsmall-p-dark mb-0">
                            {!group.primary.description || group.primary.description === "nan"
                                ? "Abstract not available"
                                : group.primary.description}
                        </p>
                    </div>
                )}
            </div>

            {/* ── Linked Paper Cards ── */}
            {showLinked && group.linked.map((s) => {
                const sid = getSid(s);
                const isExpanded = expandedLinkedIds.has(sid);
                return (
                    <div key={sid} className="p-2 me-2 ms-4 mt-2 ev-card-8 ev-radius-xsmall">
                        <div className="d-flex gap-1 flex-wrap align-items-center mb-1">
                            <PaperIdBadge study={s} className="ev-message-card3 ev-radius-xsmall ev-btn-xsmall" />
                            {(s.publication_year || s.year) && (
                                <span className="ev-message-card3 ev-radius-xsmall ev-btn-xsmall">Year {s.publication_year || s.year}</span>
                            )}
                            <BibliographicBadge study={s} />
                        </div>

                        <p className="ev-bold-med ev-small-p-dark mt-1 mb-1">{s.title}</p>
                        <p className="ev-xsmall-p-dark mb-1">{formatAuthors(s)}</p>

                        {s.description && s.description !== "nan" && (
                            <p className="ev-xsmall-p-dark mb-1">
                                Abstract: {isExpanded
                                    ? s.description
                                    : `${s.description.slice(0, 150)}${s.description.length > 150 ? "…" : ""}`}
                                {s.description.length > 150 && (
                                    <span
                                        className="ev-small-p-blue ev-bold-med cursor-pointer ms-1"
                                        onClick={() => toggleLinkedAbstract(sid)}
                                    >
                                        {isExpanded ? "Collapse" : "Expand"}
                                    </span>
                                )}
                            </p>
                        )}

                        <div className="d-flex gap-1 align-items-center mt-1">
                            <button
                                type="button"
                                className="ev-btn-lightblue-fill-round ev-btn-xsmall"
                                onClick={(e) => { e.stopPropagation(); onOpenFullText(s); }}
                            >
                                <i className="bi bi-file-earmark-pdf me-1"></i>Full Text
                            </button>
                            <button
                                type="button"
                                className="ev-btn-blue-outline-round ev-btn-xsmall"
                                onClick={() => onMakePrimary(group.id, s)}
                                title="Make this paper the primary study"
                            >
                                <i className="bi bi-star me-1"></i>Make Primary
                            </button>
                            <button
                                type="button"
                                className="ev-btn-red-outline-round ev-btn-xsmall"
                                onClick={() => onUnlink(group.id, sid)}
                            >
                                Unlink
                            </button>
                        </div>
                    </div>
                );
            })}

            {/* ── Bottom padding ── */}
            <div className="pb-2"></div>

            {showPicker && (
                <LinkPickerModal
                    leftStudies={leftStudies}
                    onClose={() => setShowPicker(false)}
                    onLink={(s) => onLink(group.id, s)}
                />
            )}
        </div>
    );
};

/* ═══════════════════════════════════════════════════════
   UniqueStudies — main component
═══════════════════════════════════════════════════════ */
const UniqueStudies = ({ fabricId, picoJson = null }) => {
    const navigate     = useNavigate();
    const API_BASE_URL = "/pceapi/slr";
    const headers = useMemo(() => ({ Authorization: GetCookie("access_token") }), []);

    /* ── list state ── */
    const [studies, setStudies]     = useState([]);
    const [counts, setCounts]       = useState({ total: 0, included: 0 });
    const [pagination, setPagination] = useState({ page: 1, size: 25, total: 0, total_pages: 1 });
    const [page, setPage]           = useState(1);
    const [pageSize, setPageSize]   = useState(25);
    const [loading, setLoading]     = useState(false);

    /* ── search ── */
    const [searchInput, setSearchInput] = useState("");
    const [searchQuery, setSearchQuery] = useState("");

    /* ── filters ── */
    const [dateFrom, setDateFrom]             = useState("");
    const [dateTo, setDateTo]                 = useState("");
    const [selectedTagIds, setSelectedTagIds] = useState([]);
    const [categoryFilter, setCategoryFilter] = useState("ALL");
    // BIBLIOGRAPHIC shows a flat list (like UNGROUPED): Included panel full width
    const leftOnlyView = categoryFilter === "UNGROUPED" || categoryFilter === "BIBLIOGRAPHIC";
    const [bibliographicIds, setBibliographicIds] = useState(() => new Set());
    const [selectedGroups, setSelectedGroups] = useState([]);
    const [clearKey, setClearKey] = useState(0);
    const [groupSortOpen, setGroupSortOpen]   = useState(false);
    const [filtersCollapsed, setFiltersCollapsed] = useState(false);

    /* ── advanced filters ── */
    const [advancedEnabled, setAdvancedEnabled] = useState(false);
    const [section1Open, setSection1Open]       = useState(false);
    const [section2Open, setSection2Open]       = useState(false);
    const [section3Open, setSection3Open]       = useState(false);
    const [advConditions, setAdvConditions]     = useState([
        { id: 1, category: "", filterOp: "contains", connector: "AND", value: "" },
    ]);
    // Which advanced-filter dropdown is expanded, keyed "<ruleId>:<which>".
    const [advOpenDropdown, setAdvOpenDropdown] = useState(null);
    const activeFilterCount =
        (searchQuery ? 1 : 0) +
        (dateFrom ? 1 : 0) +
        (dateTo ? 1 : 0) +
        selectedTagIds.length +
        (categoryFilter && categoryFilter !== "ALL" ? 1 : 0) +
        selectedGroups.length +
        (advancedEnabled ? advConditions.filter(c => c.category || c.value).length : 0);

    const clearAllFilters = () => {
        setSearchInput("");
        setSearchQuery("");
        setDateFrom("");
        setDateTo("");
        setSelectedTagIds([]);
        setCategoryFilter("ALL");
        setSelectedGroups([]);
        setAdvancedEnabled(false);
        setAdvConditions([{ id: 1, category: "", filterOp: "contains", connector: "AND", value: "" }]);
        setPage(1);
        setClearKey((k) => k + 1);
    };
    const addAdvCondition    = () => setAdvConditions((p) => [...p, { id: Date.now(), category: "", filterOp: "contains", connector: "AND", value: "" }]);
    const removeAdvCondition = (id) => setAdvConditions((p) => p.filter((c) => c.id !== id));
    const updateAdvCondition = (id, field, val) =>
        setAdvConditions((p) => p.map((c) => (c.id === id ? { ...c, [field]: val } : c)));

    /* ── group AI ── */
    const [groupFilterDropdown, setGroupFilterDropdown] = useState("ALL");
    const [groupDropdownOpen, setGroupDropdownOpen]     = useState(false);
    const [showGroupAIConfirm, setShowGroupAIConfirm]   = useState(false);

    /* ── backend group IDs (for filter) ── */
    const [groupIds, setGroupIds] = useState([]);

    /* ── expand abstract ── */
    const [expandedIds, setExpandedIds] = useState(() => new Set());
    const [hoveredLeft, setHoveredLeft] = useState(null);

    /* ── popups ── */
    const [isProcessingPopupVisible, setIsProcessingPopupVisible] = useState(false);
    const [processingMessage, setProcessingMessage]               = useState("");
    const [isErrorPopupVisible, setIsErrorPopupVisible]           = useState(false);
    const [popupErrorMessage, setPopupErrorMessage]               = useState("");
    const [isSuccessPopupVisible, setIsSuccessPopupVisible]       = useState(false);
    const [popupSuccessMessage, setPopupSuccessMessage]           = useState("");

    /* ── group panel pagination ── */
    /* ── group panel pagination ── */
    const [groupPage, setGroupPage]         = useState(1);
    const [groupPageSize, setGroupPageSize] = useState(10);

    /* ══ LOCAL GROUPING ══ */
    const [groups, setGroups]       = useState([]);
    const [groupsSaved, setGroupsSaved] = useState(false);

    /* ══ DRAG-AND-DROP ══ */
    const [draggingStudy, setDraggingStudy]     = useState(null);
    const [rightPanelHover, setRightPanelHover] = useState(false);

    /* ══ MODALS ══ */
    const [showNetworkGraph, setShowNetworkGraph]         = useState(false);
    const [pdfModal, setPdfModal]               = useState(null); // { url, title } or null
    const [showSaveConfirm, setShowSaveConfirm] = useState(false);

    /* ══ PANEL RESIZE ══ */
    const containerRef  = useRef(null);
    const [leftPct, setLeftPct]         = useState(50);
    const [isResizing, setIsResizing]   = useState(false);
    const [handleHovered, setHandleHovered] = useState(false);

    /* ── derived ── */
    const leftStudies = useMemo(() => {
        // Bibliographic view: backend already filtered — show every match,
        // including papers that belong to a group.
        if (categoryFilter === "BIBLIOGRAPHIC") return studies;
        const grouped = new Set([
            ...groups.map((g) => String(getSid(g.primary))),
            ...groups.flatMap((g) => g.linked.map((s) => String(getSid(s)))),
        ]);
        return studies.filter((s) => !grouped.has(String(getSid(s))));
    }, [studies, groups, categoryFilter]);

    const totalCount  = counts.total  || pagination.total || 0;
    const totalPages  = pagination.total_pages || Math.max(1, Math.ceil(totalCount / Math.max(1, pageSize)));
    const totalLinked = groups.reduce((n, g) => n + 1 + g.linked.length, 0);

    /* right panel: filter by selected groups when filter is active */
    const filteredGroups = useMemo(() => {
        if (!selectedGroups.length) return groups;
        return groups.filter(g => selectedGroups.some(id => String(id) === String(getSid(g.primary))));
    }, [groups, selectedGroups]);

    // reset group page when filter or groups change
    useEffect(() => { setGroupPage(1); }, [selectedGroups, groups]); // eslint-disable-line react-hooks/exhaustive-deps

    const groupTotalPages  = Math.max(1, Math.ceil(filteredGroups.length / Math.max(1, groupPageSize)));
    const paginatedGroups  = filteredGroups.slice((groupPage - 1) * groupPageSize, groupPage * groupPageSize);

    /* ── popup helpers ── */
    const closeErrorPopup   = () => setIsErrorPopupVisible(false);
    const closeSuccessPopup = async () => {
        setIsSuccessPopupVisible(false);
        await fetchData({ message: "Refreshing unique studies..." });
    };
    const showError   = (msg) => { setPopupErrorMessage(msg || "Something went wrong"); setIsErrorPopupVisible(true); };
    const showSuccess = (msg) => { setPopupSuccessMessage(msg || "Done"); setIsSuccessPopupVisible(true); };
    const handleNotAuthorized = () => {
        setIsProcessingPopupVisible(false);
        setProcessingMessage("");
        authNavigate("/login", "iam", navigate);
    };

    /* ── tags ── */
    const effectiveTagOptions = useMemo(() => {
        return buildTagOptionsFromPicoJson(picoJson);
    }, [picoJson]);

    /* ── fetch studies ── */
    const fetchData = async ({ message } = {}) => {
        if (!fabricId) return;
        setIsProcessingPopupVisible(true);
        setProcessingMessage(message || "Loading unique studies...");
        setLoading(true);
        try {
            const payload = {
                slr_fabric_id: fabricId,
                page, size: pageSize,
                search: searchQuery || null,
                start_year: dateFrom || null,
                end_year: dateTo || null,
                selected_tags: selectedTagIds?.length ? selectedTagIds : null,
                category_filter: categoryFilter !== "ALL" ? categoryFilter : null,
                sort_by_group: null,
                advanced_filters: advancedEnabled && advConditions.some(c => c.category || c.value)
                    ? { rules: advConditions.filter(c => c.category || c.value).map(c => ({ field: c.category, operator: c.filterOp || "contains", value: c.value, connector: c.connector || "AND" })) }
                        : null,
            };

            const res = await axios.post(
                `${API_BASE_URL}/study-grouping/list`,
                payload,
                { headers },
            );

            const data = res?.data;

            if (data?.status === "NotAuthorized") {
                handleNotAuthorized();
                return;
            }

            if (data?.status === "failed") {
                showError(data?.message || "Failed to load unique studies");
                return;
            }

            if (data?.status === "success") {
                const viewData = data.data || data;
                setStudies(viewData.studies || viewData.abstracts || []);
                setCounts({
                    total: viewData.counts?.total ?? viewData.total_count ?? 0,
                    included: viewData.counts?.included ?? 0,
                });
                setPagination(
                    data.pagination || {
                        page: viewData.page || 1,
                        size: viewData.size || 25,
                        total: viewData.total_count || viewData.counts?.total || 0,
                        total_pages: viewData.total_pages || 1,
                    },
                );
            } else {
                showError("Failed to load unique studies");
            }
        } catch (err) {
            console.error("Failed to fetch unique studies", err);
            showError(err?.message || "Failed to load unique studies");
        } finally {
            setLoading(false);
            setIsProcessingPopupVisible(false);
            setProcessingMessage("");
        }
    };

    const fetchGroupIds = async () => {
        if (!fabricId) return;
        try {
            const res  = await axios.post(`${API_BASE_URL}/study-grouping/group-ids`, { slr_fabric_id: fabricId }, { headers });
            const data = res?.data;
            if (data?.status === "NotAuthorized") { handleNotAuthorized(); return; }
            if (data?.status === "success") setGroupIds(data?.data?.group_ids || []);
        } catch (err) { console.error(err); }
    };

    /* ── Rebuild groups state from saved backend groups ── */
    const fetchAndRebuildGroups = async () => {
        if (!fabricId) return;
        try {
            const res = await axios.post(
                `${API_BASE_URL}/study-grouping/list`,
                // category_filter: "GROUPED" → backend applies WHERE group_number IS NOT NULL,
                // so only genuinely paired studies are returned (IS_GROUPED = true).
                // size: 2000 is safe since grouped studies are always a small subset.
                { slr_fabric_id: fabricId, page: 1, size: 2000, category_filter: "GROUPED" },
                { headers },
            );
            const data = res?.data;
            if (data?.status === "NotAuthorized") { handleNotAuthorized(); return; }
            if (data?.status !== "success") return;

            const allStudies = data?.data?.studies || [];
            // All returned studies have group_number set (GROUPED filter ensures this)
            if (!allStudies.length) return;

            // Bucket studies by group_number
            const groupMap = {};
            for (const study of allStudies) {
                const gid = String(study.group_number);
                if (!groupMap[gid]) groupMap[gid] = [];
                groupMap[gid].push(study);
            }

            // Primary = study whose study_id matches the group_number;
            // fall back to the first member so a group is never silently dropped.
            const reconstructed = Object.entries(groupMap).map(([gid, members]) => {
                const primary = members.find(s => String(s.study_id) === gid) ?? members[0];
                const linked  = members.filter(s => s !== primary);
                return {
                    id: `grp_${gid}`,
                    name: getDefaultGroupName(primary),
                    primary,
                    linked,
                };
            });

            if (reconstructed.length) {
                setGroups(reconstructed);
                setGroupsSaved(true);
            }
        } catch (err) {
            console.error("Failed to rebuild groups", err);
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { fetchData({ message: "Loading unique studies..." }); }, [
        fabricId, page, pageSize, searchQuery, dateFrom, dateTo,
        selectedTagIds, categoryFilter, clearKey,
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { fetchGroupIds(); fetchAndRebuildGroups(); }, [fabricId]);

    useEffect(() => {
        if (!fabricId) return;
        let cancelled = false;
        axios.post(`${API_BASE_URL}/qc/bibliographic-study-ids`, { slr_fabric_config_id: fabricId }, { headers })
            .then((res) => {
                if (!cancelled && res.data?.status === "success") {
                    setBibliographicIds(new Set((res.data.study_ids || []).map(String)));
                }
            })
            .catch((err) => console.error("Bibliographic ids fetch failed:", err));
        return () => { cancelled = true; };
    }, [fabricId]); // eslint-disable-line

    // Filter is hidden when there are no bibliographic papers — never leave it stuck on BIBLIOGRAPHIC
    useEffect(() => {
        if (bibliographicIds.size === 0 && categoryFilter === "BIBLIOGRAPHIC") setCategoryFilter("ALL");
    }, [bibliographicIds, categoryFilter]);
    useEffect(() => {
        if (pagination.total_pages > 0 && page > pagination.total_pages) setPage(pagination.total_pages);
    }, [pagination.total_pages]);

    /* ── panel resize ── */
    const handleResizeMouseDown = useCallback((e) => {
        e.preventDefault();
        setIsResizing(true);
        const container = containerRef.current;
        if (!container) return;
        const startX     = e.clientX;
        const startPct   = leftPct;
        const containerW = container.getBoundingClientRect().width;
        const onMove = (ev) => {
            const dx     = ev.clientX - startX;
            const newPct = Math.min(75, Math.max(25, startPct + (dx / containerW) * 100));
            setLeftPct(newPct);
        };
        const onUp = () => {
            setIsResizing(false);
            document.removeEventListener("mousemove", onMove);
            document.removeEventListener("mouseup",  onUp);
        };
        document.addEventListener("mousemove", onMove);
        document.addEventListener("mouseup",   onUp);
    }, [leftPct]);

    useEffect(() => {
        if (isResizing) {
            document.body.style.userSelect = "none";
            document.body.style.cursor     = "ew-resize";
        } else {
            document.body.style.userSelect = "";
            document.body.style.cursor     = "";
        }
        return () => { document.body.style.userSelect = ""; document.body.style.cursor = ""; };
    }, [isResizing]);

    /* ── open full text PDF in modal ── */
    const openFullText = (study) => {
        const docId = study?.doc_id;
        if (!docId) {
            showError("Full text PDF not linked for this study");
            return;
        }
        setPdfModal({ docId, title: study?.title || "Full Text" });
    };
    /* ── drag handlers ── */
    const handleDragStart = (e, study) => { setDraggingStudy(study); e.dataTransfer.effectAllowed = "move"; };
    const handleDragEnd   = () => { setDraggingStudy(null); setRightPanelHover(false); };

    const handleRightPanelDragOver  = (e) => { e.preventDefault(); setRightPanelHover(true); };
    const handleRightPanelDragLeave = (e) => { if (!e.currentTarget.contains(e.relatedTarget)) setRightPanelHover(false); };
    const handleRightPanelDrop      = (e) => {
        e.preventDefault();
        if (!draggingStudy) return;
        const baseName = getDefaultGroupName(draggingStudy);
        setGroups((prev) => {
            const name = resolveGroupName(baseName, prev);
            return [...prev, { id: `grp_${Date.now()}`, name, primary: draggingStudy, linked: [] }];
        });
        setGroupsSaved(false);
        setDraggingStudy(null);
        setRightPanelHover(false);
    };

    /* ── link / remove / change primary ── */
    const handleLinkPaper  = (groupId, study) => {
        setGroups((prev) => prev.map((g) => g.id === groupId ? { ...g, linked: [...g.linked, study] } : g));
        setGroupsSaved(false);
    };
    const handleRemoveGroup = (groupId) => {
        setGroups((prev) => prev.filter((g) => g.id !== groupId));
        setGroupsSaved(false);
    };
    const handleUnlinkPaper = (groupId, studyId) => {
        setGroups((prev) => prev.map((g) =>
            g.id === groupId
                ? { ...g, linked: g.linked.filter((s) => String(getSid(s)) !== String(studyId)) }
                : g
        ));
        setGroupsSaved(false);
    };
    const handleRenameGroup = (groupId, newName) => {
        setGroups((prev) => prev.map((g) => g.id === groupId ? { ...g, name: newName } : g));
        setGroupsSaved(false);
    };
    const handleMakePrimary = (groupId, study) => {
        setGroups((prev) => prev.map((g) => {
            if (g.id !== groupId) return g;
            const oldPrimary = g.primary;
            const newLinked = g.linked
                .filter((s) => String(getSid(s)) !== String(getSid(study)))
                .concat(oldPrimary);
            return { ...g, primary: study, linked: newLinked };
        }));
        setGroupsSaved(false);
    };


    /* ── save groups ── */
    const handleSaveGroups = () => {
        if (!groups.length) { showError("Create at least one group before saving"); return; }
        setShowSaveConfirm(true);
    };
    const confirmSaveGroups = async () => {
        setShowSaveConfirm(false);
        if (!fabricId || !groups.length) return;
        setIsProcessingPopupVisible(true);
        setProcessingMessage("Saving group selections...");
        try {
            const mappings = groups.flatMap((g) => {
                const gid = Number(getSid(g.primary));
                return [
                    { study_id: gid, group_id: gid },
                    ...g.linked.map((s) => ({ study_id: Number(getSid(s)), group_id: gid })),
                ];
            });
            const res  = await axios.post(
                `${API_BASE_URL}/study-grouping/save`,
                { slr_fabric_id: fabricId, mappings },
                { headers },
            );
            const data = res?.data;
            if (data?.status === "NotAuthorized") { handleNotAuthorized(); return; }
            if (data?.status === "success" || data?.status === "partial") {
                await fetchGroupIds();
                await fetchAndRebuildGroups();
                setGroupsSaved(true);
                showSuccess(data?.message || "Group selections saved successfully");
            } else {
                showError(data?.message || "Failed to save group selections");
            }
        } catch (err) {
            showError(err?.message || "Failed to save group selections");
        } finally {
            setIsProcessingPopupVisible(false);
            setProcessingMessage("");
        }
    };

    /* ── AI grouping ── */
    const handleGroupStudiesAI = () => setShowGroupAIConfirm(true);

    /* ── Download grouped papers as zip (one folder per group) ── */
    const handleDownloadGroups = async () => {
        if (!groups.length) { showError("No groups to download."); return; }
        setIsProcessingPopupVisible(true);
        setProcessingMessage("Preparing grouped download…");
        try {
            const zip = new JSZip();
            for (const group of groups) {
                const folderName = group.name || `Group_${group.id}`;
                const folder = zip.folder(folderName);
                const allPapers = [group.primary, ...group.linked].filter(Boolean);
                for (const paper of allPapers) {
                    const docId = paper?.doc_id;
                    if (!docId) continue;
                    setProcessingMessage(`Fetching ${paper.title?.slice(0, 40) || "document"}…`);
                    try {
                        const res = await axios.post(
                            `${API_BASE_URL}/full-text-screening/preview`,
                            { fabric_id: fabricId, doc_id: docId },
                            { headers }
                        );
                        const url = res.data?.presigned_url || res.data?.data?.presigned_url;
                        if (!url) continue;
                        const blob = await fetch(url).then(r => r.blob());
                        const fileName = `${paper.study_id ?? docId}_${(paper.title || "document").slice(0, 40).replace(/[^\w\s-]/g, "").trim()}.pdf`;
                        folder.file(fileName, blob);
                    } catch (e) {
                        console.error("Failed to fetch doc", docId, e);
                    }
                }
            }
            setProcessingMessage("Creating zip…");
            const zipBlob = await zip.generateAsync({ type: "blob" });
            const a = document.createElement("a");
            a.href = URL.createObjectURL(zipBlob);
            a.download = "grouped_studies.zip";
            document.body.appendChild(a); a.click(); document.body.removeChild(a);
            URL.revokeObjectURL(a.href);
        } catch (err) {
            showError("Failed to create zip file.");
        } finally {
            setIsProcessingPopupVisible(false);
            setProcessingMessage("");
        }
    };
    const confirmGroupStudiesAI = async () => {
        setShowGroupAIConfirm(false);
        if (!fabricId) return;
        const mode = groupFilterDropdown === "UNGROUPED" ? "only_ungrouped" : "all";
        setIsProcessingPopupVisible(true);
        setProcessingMessage("Grouping studies using AI...");
        try {
            const res  = await axios.post(`${API_BASE_URL}/study-grouping/run-ai`, { slr_fabric_id: fabricId, mode }, { headers });
            const data = res?.data;
            if (data?.status === "NotAuthorized") { handleNotAuthorized(); return; }
            if (data?.status === "success") {
                await fetchGroupIds();
                await fetchAndRebuildGroups();
                setGroupsSaved(true);
                showSuccess(data?.message || "AI grouping completed successfully");
            } else {
                showError(data?.message || "Failed to start AI grouping");
            }
        } catch (err) {
            showError(err?.message || "Failed to start AI grouping");
        } finally {
            setIsProcessingPopupVisible(false);
            setProcessingMessage("");
        }
    };

    /* ── misc helpers ── */
    const toggleTag = (tagId) => {
        if (!tagId) return;
        setSelectedTagIds((prev) => {
            const next = new Set(prev || []);
            if (next.has(tagId)) next.delete(tagId); else next.add(tagId);
            return Array.from(next);
        });
        setPage(1);
    };
    const handleSearch         = () => { setSearchQuery(searchInput.trim()); setPage(1); };
    const handleSearchKeyPress = (e) => { if (e.key === "Enter") handleSearch(); };
    const toggleExpanded = (id) => {
        setExpandedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    /* ═══════════════════════════════════════
       RENDER
    ═══════════════════════════════════════ */
    return (
        <BibliographicIdsContext.Provider value={bibliographicIds}>
        <div className="h-100 d-flex flex-column">

            {/* ── HEADER ── */}
            <div className="d-flex justify-content-between align-items-center mb-2">
                <div className="d-flex w-70 gap-3 align-items-center">
                    <p className="ev-med-p-blue ev-bold-med mb-0 ms-2 text-nowrap">Unique Studies</p>
                    <div className="position-relative w-55">
                        <input
                            type="text"
                            className="form-control search-input ms-0"
                            placeholder="Search by title, abstract or author name"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            onKeyPress={handleSearchKeyPress}
                        />
                        <button className="ev-btn-lightblue-fill-round search-bar-button" onClick={handleSearch}>
                            <i className="bi bi-search"></i>
                        </button>
                    </div>
                </div>

                <div className="d-flex gap-2 align-items-center">
                    <button
                        className="ev-btn-blue-outline-round ev-btn-small text-nowrap"
                        onClick={handleDownloadGroups}
                        disabled={!groups.length}
                        title="Download grouped papers as zip"
                    >
                        <i className="bi bi-download me-1"></i>Download Groups
                    </button>
                    <div className="d-flex gap-1 ev-card-4 p-1 position-relative">
                        <button
                            className="ev-btn-find-matches ev-btn-small text-nowrap pe-4"
                            onClick={handleGroupStudiesAI}
                        >
                            Group Studies Using AI
                        </button>
                        <div>
                            <button
                                className="ev-btn-blue-outline-round ev-btn-small ev-radius-large d-flex align-items-center gap-5 px-2 ev-bold-med text-nowrap"
                                onClick={() => setGroupDropdownOpen((p) => !p)}
                            >
                                {groupFilterDropdown === "ALL" ? "All" : "Only Ungrouped"}
                                <i className={`bi ${groupDropdownOpen ? "bi-chevron-up" : "bi-chevron-down"} ev-fs-12`}></i>
                            </button>
                            {groupDropdownOpen && (
                                <div className="ev-card-4 position-absolute top-100 end-0 mt-1 p-2 ev-radius-xsmall shadow-sm w-100 z-3 d-flex flex-column gap-1">
                                    {[{ value: "ALL", label: "All" }, { value: "UNGROUPED", label: "Only Ungrouped" }].map((opt) => (
                                        <div
                                            key={opt.value}
                                            className={`ev-card-2 px-3 py-1 cursor-pointer ev-small-p-dark ev-radius-xsmall ev-bold-med ${groupFilterDropdown === opt.value ? "ev-message-card1" : ""}`}
                                            onClick={() => { setGroupFilterDropdown(opt.value); setGroupDropdownOpen(false); setPage(1); }}
                                        >
                                            {opt.label}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    <button
                        className="ev-btn-lightblue-fill-round ev-btn-reg text-nowrap"
                        onClick={() => { fetchGroupIds(); fetchData({ message: "Refreshing unique studies..." }); }}
                        disabled={loading}
                    >
                        <i className="bi bi-arrow-repeat"></i> Refresh
                    </button>
                </div>
            </div>

            {/* ── BODY: filter col + right col ── */}
            <div className="flex-grow-1 overflow-hidden">
                <div className="row gx-3 h-100">

                {/* ═══ LEFT: FILTER PANEL ═══ */}
                <div className={filtersCollapsed ? "col-auto" : "ev-filter-panel-fixed"}>
                    <div className={`${filtersCollapsed ? "" : "vh-65 scrollable-element pe-1"}`}>
                <div
                className="ev-card-2 ev-radius-xsmall d-flex align-items-center justify-content-between p-1 mb-2 cursor-pointer gap-2 position-relative"
                  onClick={() => setFiltersCollapsed(prev => !prev)}
              >
                  {!filtersCollapsed && (
                    <>
                      <p className="ev-med-p-dark-blue ev-bold-med mb-0 position-absolute start-50 translate-middle-x">Filtering</p>
                      {activeFilterCount > 0 ? (
                        <div></div>
                      ) : <div></div>}
                    </>
                  )}
                              <i className={`bi ${filtersCollapsed ? "bi-funnel px-1" : "bi-chevron-left"} ev-med-p-dark-blue`}></i>
                              {filtersCollapsed && activeFilterCount > 0 && (
                                <span className="ev-message-card3 ev-bold-med ev-radius-large"
                                  style={{ fontSize: "var(--px-10)", padding: "0 6px" }}>
                                  {activeFilterCount}
                                </span>
                              )}
                        </div>

                        {!filtersCollapsed && (
                            <div className="d-flex flex-column gap-2">

                                {/* SECTION 1 — Basic Filters */}
                                <div className="ev-card-8 ev-radius-small p-2">
                                    <div
                                        className="d-flex align-items-center gap-2 cursor-pointer"
                                        onClick={() => setSection1Open((p) => !p)}
                                    >
                                        <span
                                            className="ev-message-card3 ev-bold-med d-flex align-items-center justify-content-center ev-radius-large flex-shrink-0"
                                            style={{ width: 22, height: 22, fontSize: "var(--px-10)" }}
                                        >
                                            1
                                        </span>
                                        <p className="ev-small-p-dark ev-bold-med mb-0 flex-grow-1">Basic Filters</p>
                                        <i className={`bi ${section1Open ? "bi-chevron-up" : "bi-chevron-down"} ev-xsmall-p-dark`}></i>
                                    </div>

                                    {section1Open && (
                                        <div className="mt-2">
                                            {/* Publication Year */}
                                            <p className="ev-xsmall-p-dark ev-bold-med mb-1">Publication Year</p>
                                            <YearRangePicker
                                              from={dateFrom}
                                              to={dateTo}
                                              onFromChange={(v) => { setDateFrom(v); setPage(1); }}
                                              onToChange={(v) => { setDateTo(v); setPage(1); }}
                                            />

                                            {/* Category */}
                                            <p className="ev-xsmall-p-dark ev-bold-med mb-1">Category</p>
                                            <div className="d-flex flex-wrap gap-1 mb-2">
                                                {["ALL", "GROUPED", "UNGROUPED", ...(bibliographicIds.size > 0 ? ["BIBLIOGRAPHIC"] : [])].map((cat) => (
                                                    <button
                                                        key={cat}
                                                        className={
                                                            categoryFilter === cat
                                                                ? "ev-btn-blue-fill-round ev-btn-small ev-radius-xsmall ev-bold-reg flex-fill"
                                                                : "ev-btn-lightblue-fill-round ev-btn-small ev-radius-xsmall ev-bold-med flex-fill"
                                                        }
                                                        onClick={() => { setCategoryFilter(cat); setPage(1); }}
                                                    >
                                                        {cat.charAt(0) + cat.slice(1).toLowerCase()}
                                                    </button>
                                                ))}
                                            </div>

                                            {/* Sort by Group */}
                                            <div className="position-relative">
                                                <button
                                                    className="w-100 d-flex justify-content-between align-items-center ev-message-card2 px-2 py-1 ev-radius-xsmall"
                                                    onClick={() => setGroupSortOpen((p) => !p)}
                                                >
                                                    <span className="ev-bold-med ev-xsmall-p-dark">
                                                        Filter by Group {selectedGroups.length > 0 && `(${selectedGroups.length})`}
                                                    </span>
                                                    <i className={`bi ${groupSortOpen ? "bi-chevron-down" : "bi-chevron-right"} ev-xsmall-p-dark`}></i>
                                                </button>
                                                {groupSortOpen && (
                                                    <div className="ev-card-2 mt-1 py-1 ev-radius-xsmall">
                                                        <div className="d-flex flex-column gap-1 scrollable-element pe-1 vh-20 p-1">
                                                            {groups.length === 0 && groupIds.length === 0 ? (
                                                                <span className="ev-xsmall-p-dark px-2 py-1 d-block">No groups available</span>
                                                            ) : groups.length > 0 ? (
                                                                /* groups loaded — show actual names */
                                                                groups.map((g) => {
                                                                    const gId = getSid(g.primary);
                                                                    const isChecked = selectedGroups.some(id => String(id) === String(gId));
                                                                    return (
                                                                        <label key={gId} className="d-flex align-items-center gap-2 px-1 py-1 cursor-pointer ev-small-p-dark">
                                                                            <input
                                                                                type="checkbox" checked={isChecked}
                                                                                onChange={() => {
                                                                                    setSelectedGroups((prev) =>
                                                                                        isChecked
                                                                                            ? prev.filter((id) => String(id) !== String(gId))
                                                                                            : [...prev, gId]
                                                                                    );
                                                                                    setPage(1);
                                                                                }}
                                                                            />
                                                                            {g.name || formatGroupLabel(gId)}
                                                                        </label>
                                                                    );
                                                                })
                                                            ) : (
                                                                /* groups not yet loaded — show raw ids */
                                                                groupIds.map((gId) => {
                                                                    const isChecked = selectedGroups.includes(gId);
                                                                    return (
                                                                        <label key={gId} className="d-flex align-items-center gap-2 px-1 py-1 cursor-pointer ev-small-p-dark">
                                                                            <input
                                                                                type="checkbox" checked={isChecked}
                                                                                onChange={() => {
                                                                                    setSelectedGroups((prev) =>
                                                                                        isChecked ? prev.filter((g) => g !== gId) : [...prev, gId]
                                                                                    );
                                                                                    setPage(1);
                                                                                }}
                                                                            />
                                                                            {formatGroupLabel(gId)}
                                                                        </label>
                                                                    );
                                                                })
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* SECTION 2 — Tags */}
                                <div className="ev-card-8 ev-radius-small p-2">
                                    <div
                                        className="d-flex align-items-center gap-2 cursor-pointer"
                                        onClick={() => setSection2Open((p) => !p)}
                                    >
                                        <span
                                            className="ev-message-card3 ev-bold-med d-flex align-items-center justify-content-center ev-radius-large flex-shrink-0"
                                            style={{ width: 22, height: 22, fontSize: "var(--px-10)" }}
                                        >
                                            2
                                        </span>
                                        <div className="d-flex align-items-center gap-2 flex-grow-1">
                                            <p className="ev-small-p-dark ev-bold-med mb-0">Tags</p>
                                            {selectedTagIds?.length > 0 && (
                                                <span className="ev-message-card3 ev-btn-xsmall">
                                                    {selectedTagIds.length} selected
                                                </span>
                                            )}
                                        </div>
                                        <i className={`bi ${section2Open ? "bi-chevron-up" : "bi-chevron-down"} ev-xsmall-p-dark`}></i>
                                    </div>

                                    {section2Open && (
                                        <div className="mt-2">
                                            <TagFilterPanel
                                                tagOptions={effectiveTagOptions}
                                                selectedTagIds={selectedTagIds}
                                                onToggleTag={toggleTag}
                                                tagsHeight="vh-15"
                                            />
                                        </div>
                                    )}
                                </div>

                                {/* SECTION 3 — Advanced Filtering */}
                                <div className="ev-card-8 ev-radius-small p-2">
                                    <div
                                        className="d-flex align-items-center gap-2 cursor-pointer"
                                        onClick={() => setSection3Open((p) => !p)}
                                    >
                                        <span
                                            className="ev-message-card3 ev-bold-med d-flex align-items-center justify-content-center ev-radius-large flex-shrink-0"
                                            style={{ width: 22, height: 22, fontSize: "var(--px-10)" }}
                                        >
                                            3
                                        </span>
                                        <p className="ev-small-p-dark ev-bold-med mb-0 flex-grow-1">Advanced Filtering</p>
                                        <div
                                            className="form-check form-switch fs-6 mb-0 ms-auto"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                const newVal = !advancedEnabled;
                                                setAdvancedEnabled(newVal);
                                                setSection3Open(newVal);
                                            }}
                                        >
                                            <input className="form-check-input cursor-pointer" type="checkbox" checked={advancedEnabled} onChange={() => {}} />
                                        </div>
                                    </div>

                                    {!section3Open && advancedEnabled && advConditions.some((c) => c.category || c.value) && (
                                        <div className="d-flex justify-content-between align-items-center mt-2">
                                            <span className="ev-small-p-dark">
                                                {advConditions.filter((c) => c.category || c.value).length} conditions applied
                                            </span>
                                        </div>
                                    )}

                                    {section3Open && (
                                        <div className="mt-2">
                                            <div>
                                                {advConditions.map((cond, idx) => (
                                                    <div key={cond.id}>
                                                        {idx > 0 && (
                                                            <div className="d-flex justify-content-center mb-2">
                                                                <InlineSelect
                                                                  id={`${cond.id}:connector`}
                                                                  openId={advOpenDropdown}
                                                                  setOpenId={setAdvOpenDropdown}
                                                                  width={90}
                                                                  value={cond.connector || "AND"}
                                                                  placeholder="AND"
                                                                  options={["AND", "OR"].map((v) => ({
                                                                    value: v,
                                                                    label: v,
                                                                    onSelect: () => updateAdvCondition(cond.id, "connector", v),
                                                                  }))}
                                                                />
                                                            </div>
                                                        )}
                                                        <div className="d-flex align-items-start gap-1 mb-2">
                                                            <InlineSelect
                                                              id={`${cond.id}:field`}
                                                              openId={advOpenDropdown}
                                                              setOpenId={setAdvOpenDropdown}
                                                              className="flex-grow-1"
                                                              value={cond.category}
                                                              placeholder="Field"
                                                              options={ADV_FIELD_OPTIONS.map((o) => ({
                                                                ...o,
                                                                onSelect: () => updateAdvCondition(cond.id, "category", o.value),
                                                              }))}
                                                            />
                                                            <InlineSelect
                                                              id={`${cond.id}:operator`}
                                                              openId={advOpenDropdown}
                                                              setOpenId={setAdvOpenDropdown}
                                                              className="flex-grow-1"
                                                              value={cond.filterOp || "contains"}
                                                              placeholder="Contains"
                                                              options={ADV_OPERATOR_OPTIONS.map((o) => ({
                                                                ...o,
                                                                onSelect: () => updateAdvCondition(cond.id, "filterOp", o.value),
                                                              }))}
                                                            />
                                                            {!["is_empty", "is_not_empty", "is_null", "is_not_null"].includes(cond.filterOp) && (
                                                                <input
                                                                    type="text"
                                                                    className="formField4 ev-btn-small ev-radius-xsmall flex-grow-1"
                                                                    placeholder="Value"
                                                                    value={cond.value}
                                                                    onChange={(e) => updateAdvCondition(cond.id, "value", e.target.value)}
                                                                />
                                                            )}
                                                            {advConditions.length > 1 && (
                                                                <button
                                                                    className="ev-btn-light-fill ev-btn-xsmall ev-radius-xsmall border-0"
                                                                    onClick={() => removeAdvCondition(cond.id)}
                                                                >
                                                                    <i className="bi bi-x-lg"></i>
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                                <button
                                                    className="ev-btn-text-only-fill ev-btn-small p-0"
                                                    onClick={addAdvCondition}
                                                >
                                                    <i className="bi bi-plus-lg me-1"></i> Add rule
                                                </button>
                                            </div>

                                            <div className="d-flex gap-1 mt-3">
                                                <button
                                                    className="ev-btn-blue-outline-round ev-btn-reg ev-radius-small w-50"
                                                    onClick={() => setAdvConditions([{ id: 1, category: "", filterOp: "contains", connector: "AND", value: "" }])}
                                                >
                                                    Clear
                                                </button>
                                                <button
                                                    className="ev-btn-blue-fill-round ev-btn-reg ev-radius-xsmall w-50"
                                                    onClick={() => { setPage(1); fetchData({ message: "Applying filters..." }); }}
                                                >
                                                    Apply Filters
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* ═══ RIGHT: METRIC ROW + TWO-PANEL SPLIT ═══ */}
                <div className="col h-100 d-flex flex-column">

                    {/* Metric row */}
                    <div className="ev-card-8 d-flex align-items-center ev-radius-small mb-2 overflow-hidden px-2">
                        <div className="d-flex flex-column align-items-center text-center flex-grow-1 py-2">
                            <span className="ev-bold-large text-primary">{totalCount}</span>
                            <span className="ev-bold-reg text-primary">Total</span>
                        </div>
                        <div className="vr my-2"></div>
                        <div className="d-flex flex-column align-items-center text-center flex-grow-1 py-2">
                            <span className="ev-bold-large text-muted">{leftStudies.length}</span>
                            <span className="ev-bold-reg text-muted">Unassigned</span>
                        </div>
                        <div className="vr my-2"></div>
                        <div className="d-flex flex-column align-items-center text-center flex-grow-1 py-2">
                            <span className="ev-bold-large text-success">{groups.length}</span>
                            <span className="ev-bold-reg text-success">Groups</span>
                        </div>
                        <div className="vr my-2"></div>
                        <div className="d-flex flex-column align-items-center text-center flex-grow-1 py-2">
                            <span className="ev-bold-large text-primary">{totalLinked}</span>
                            <span className="ev-bold-reg text-primary">Linked</span>
                        </div>
                    </div>

                    {/* Action buttons row */}
                    <div className="d-flex gap-2 align-items-center mb-2 pe-1">
                        {groups.length > 0 && !groupsSaved && (
                            <button type="button" className="ev-btn-yellow-fill-round ev-btn-reg ev-bold-med text-nowrap" onClick={handleSaveGroups}>
                                <i className="bi bi-check-circle me-1"></i>Save Groups
                            </button>
                        )}
                        <button
                            type="button"
                            className="ev-btn-blue-fill-round ev-btn-reg text-nowrap"
                            onClick={() => setShowNetworkGraph(true)}
                            disabled={groups.length === 0}
                        >
                            <i className="bi bi-diagram-3 me-1"></i>View Grouped Papers
                        </button>
                    </div>

                    {/* Two-panel split */}
                    <div ref={containerRef} className="d-flex flex-grow-1 overflow-hidden">

                        {/* ── Included Papers panel ── */}
                        {categoryFilter !== "GROUPED" && (
                        <div
                            className="d-flex flex-column overflow-hidden flex-shrink-0 vh-60"
                            style={{ width: leftOnlyView ? "100%" : `${leftPct}%` }}
                        >
                        <div className="ev-card-8 ev-radius-small p-2 d-flex flex-column flex-grow-1 overflow-hidden" style={{ border: "1.5px solid var(--color14)" }}>
                            <p className="ev-small-p-dark ev-bold-med mb-2">
                                <i className="bi bi-files me-1"></i>
                                Included Papers <span className="ev-small-p-blue">({leftStudies.length})</span>
                            </p>

                            <div className="flex-grow-1 d-flex flex-column gap-2 overflow-auto scrollable-element pe-1">
                                {!loading && leftStudies.map((item) => {
                                    const sid        = getSid(item);
                                    const isExpanded = expandedIds.has(sid);
                                    const isDragging = draggingStudy && getSid(draggingStudy) === sid;
                                    return (
                                        <div
                                            key={sid}
                                            draggable
                                            onDragStart={(e) => handleDragStart(e, item)}
                                            onDragEnd={handleDragEnd}
                                            className={`ev-card-8 ev-radius-small p-3 py-2${hoveredLeft === sid ? " active" : ""}`}
                                            onMouseEnter={() => setHoveredLeft(sid)}
                                            onMouseLeave={() => setHoveredLeft(null)}
                                            style={{ opacity: isDragging ? 0.45 : 1, cursor: "grab" }}
                                        >
                                            <div className="d-flex justify-content-between align-items-start mb-1">
                                                <div className="d-flex gap-1 align-items-center flex-wrap">
                                                    <PaperIdBadge study={item} />
                                                    <span className="ev-message-card3 ev-radius-xsmall ev-btn-xsmall">Year {item.publication_year || item.year}</span>
                                                    <BibliographicBadge study={item} />
                                                    <DecisionBadge decision={item.final_decision || item.status} />
                                                </div>
                                                <i className="bi bi-grip-vertical ev-small-p-dark opacity-50 flex-shrink-0 ms-1"></i>
                                            </div>
                                            <p className="ev-bold-med ev-small-p-dark mt-2 mb-1" style={{ overflowWrap: "anywhere" }}>{item.title}</p>
                                            <p className="ev-xsmall-p-dark mb-1" style={{ overflowWrap: "anywhere" }}>{formatAuthors(item)}</p>
                                            <div className="d-flex justify-content-start align-items-center gap-2 mt-1">
                                                <div
                                                    className="d-flex align-items-center gap-1 ev-small-p-blue ev-bold-med cursor-pointer"
                                                    onClick={() => toggleExpanded(sid)}
                                                >
                                                    <span>Abstract</span>
                                                    <i className={`bi ${isExpanded ? "bi-chevron-up" : "bi-chevron-down"} ev-fs-12`} />
                                                </div>
                                                <button
                                                    type="button"
                                                    className="ev-btn-lightblue-fill-round ev-btn-xsmall"
                                                    onClick={(e) => { e.stopPropagation(); openFullText(item); }}
                                                    title="View full text PDF"
                                                >
                                                    <i className="bi bi-file-earmark-pdf me-1"></i>Full Text
                                                </button>
                                            </div>
                                            {isExpanded && (
                                                <div className="mt-2 ev-card-8 p-2 ev-radius-xsmall">
                                                    <p className="ev-xsmall-p-dark mb-0">
                                                        {!item.description || item.description === "nan"
                                                            ? "Abstract not available"
                                                            : item.description}
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}

                                {!loading && leftStudies.length === 0 && (
                                    <div className="d-flex flex-column align-items-center justify-content-center text-center flex-grow-1 py-5">
                                        <i className="bi bi-check2-circle ev-fs-24 ev-small-p-blue mb-2 d-block"></i>
                                        <p className="ev-small-p-dark mb-0">All papers have been assigned to groups.</p>
                                    </div>
                                )}
                            </div>

                            {/* Pagination */}
                            {!loading && (
                                <div className="d-flex justify-content-between align-items-center mt-2">
                                    <span className="ev-xsmall-p-dark">Showing {leftStudies.length} of {totalCount}</span>
                                    <div className="d-flex align-items-center gap-2">
                                        <button className="ev-btn-light-fill ev-btn-xsmall" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
                                            <i className="bi bi-chevron-left"></i>
                                        </button>
                                        <button className="ev-btn-light-fill ev-btn-xsmall" disabled>
                                            {page} / {totalPages || 1}
                                        </button>
                                        <button className="ev-btn-light-fill ev-btn-xsmall" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
                                            <i className="bi bi-chevron-right"></i>
                                        </button>
                                    </div>
                                    <PaginationControls initialSize={pageSize} onSizeChange={(sz) => { setPageSize(sz); setPage(1); }} />
                                </div>
                            )}
                        </div>
                        </div>
                        )}

                        {/* ── Resize handle ── */}
                        {categoryFilter === "ALL" && (
                        <div
                            className="flex-shrink-0 position-relative"
                            style={{ width: 8, cursor: "ew-resize", zIndex: 10 }}
                            onMouseEnter={() => setHandleHovered(true)}
                            onMouseLeave={() => setHandleHovered(false)}
                            onMouseDown={handleResizeMouseDown}
                        >
                            <div
                                style={{
                                    position: "absolute",
                                    top: "50%",
                                    left: "50%",
                                    transform: "translate(-50%, -50%)",
                                    width: 3,
                                    height: 50,
                                    borderRadius: 2,
                                    background: handleHovered || isResizing ? "var(--color9)" : "var(--color14)",
                                    opacity: handleHovered || isResizing ? 1 : 0,
                                    transition: "opacity 0.15s, background 0.15s",
                                    pointerEvents: "none",
                                }}
                            />
                        </div>
                        )}

                        {/* ── Primary Papers panel ── */}
                        {!leftOnlyView && (
                        <div className="d-flex flex-column overflow-hidden flex-grow-1 vh-60">
                        <div className="ev-card-8 ev-radius-small p-2 d-flex flex-column flex-grow-1 overflow-hidden" style={{ border: "1.5px solid var(--color25)" }}>
                            <p className="ev-small-p-dark ev-bold-med mb-2">
                                <i className="bi bi-collection me-1"></i>
                                Primary Papers <span className="ev-small-p-blue">({filteredGroups.length}{selectedGroups.length > 0 ? ` of ${groups.length}` : ""})</span>
                            </p>

                            <div
                                className="flex-grow-1 scrollable-element ps-1 d-flex flex-column gap-2"
                                style={{
                                    overflowY: "auto",
                                    borderRadius: 8,
                                    border: rightPanelHover && draggingStudy ? "2px dashed var(--color9)" : "2px dashed transparent",
                                    transition: "border-color 0.15s",
                                    minHeight: 200,
                                }}
                                onDragOver={handleRightPanelDragOver}
                                onDragLeave={handleRightPanelDragLeave}
                                onDrop={handleRightPanelDrop}
                            >
                                {filteredGroups.length === 0 && (
                                    <div
                                        className="d-flex flex-column align-items-center justify-content-center text-center flex-grow-1"
                                        style={{ minHeight: 200, pointerEvents: "none" }}
                                    >
                                        <i className="bi bi-arrow-left ev-fs-24 ev-small-p-blue mb-2 d-block"></i>
                                        <p className="ev-small-p-dark mb-1 ev-bold-med">Drag papers here</p>
                                        <p className="ev-xsmall-p-dark mb-0">Drop from the left panel to create a primary study group</p>
                                    </div>
                                )}

                                {paginatedGroups.map((group, gi) => (
                                    <PrimaryGroupCard
                                        key={group.id}
                                        group={group}
                                        groupIndex={gi}
                                        leftStudies={leftStudies}
                                        onLink={handleLinkPaper}
                                        onUnlink={handleUnlinkPaper}
                                        onMakePrimary={handleMakePrimary}
                                        onRenameGroup={handleRenameGroup}
                                        onRemoveGroup={() => handleRemoveGroup(group.id)}
                                        onOpenFullText={openFullText}
                                    />
                                ))}
                            </div>
                            {/* pagination */}
                            <div className="d-flex justify-content-between align-items-center mt-2">
                                <span className="ev-xsmall-p-dark">Showing {paginatedGroups.length} of {filteredGroups.length}</span>
                                <div className="d-flex align-items-center gap-2">
                                    <button className="ev-btn-light-fill ev-btn-xsmall" disabled={groupPage === 1} onClick={() => setGroupPage((p) => p - 1)}>
                                        <i className="bi bi-chevron-left"></i>
                                    </button>
                                    <button className="ev-btn-light-fill ev-btn-xsmall" disabled>
                                        {groupPage} / {groupTotalPages}
                                    </button>
                                    <button className="ev-btn-light-fill ev-btn-xsmall" disabled={groupPage >= groupTotalPages} onClick={() => setGroupPage((p) => p + 1)}>
                                        <i className="bi bi-chevron-right"></i>
                                    </button>
                                </div>
                                <PaginationControls initialSize={groupPageSize} onSizeChange={(sz) => { setGroupPageSize(sz); setGroupPage(1); }} />
                            </div>
                        </div>
                        </div>
                        )}
                    </div>
                </div>
                </div>
            </div>

            {/* ── MODALS ── */}
            {showNetworkGraph && (
                <NetworkGraphModal groups={groups} onClose={() => setShowNetworkGraph(false)} />
            )}
            {pdfModal && (
                <DocumentPreview
                    isOpen={!!pdfModal}
                    onClose={() => setPdfModal(null)}
                    docId={pdfModal.docId}
                    documentName={pdfModal.title}
                    bucket_type="input"
                />
            )}
            <ConfirmationModal
                show={showGroupAIConfirm}
                title="Group Studies Using AI?"
                message={
                    groupFilterDropdown === "UNGROUPED"
                        ? "AI will group only ungrouped studies, preserving existing groups."
                        : "AI will regroup all studies from scratch. Existing groups will be reset."
                }
                confirmButtonText="Yes, Group"
                cancelButtonText="Cancel"
                onConfirm={confirmGroupStudiesAI}
                onClose={() => setShowGroupAIConfirm(false)}
            />
            <ConfirmationModal
                show={showSaveConfirm}
                title="Save Group Selections?"
                message={`Save ${groups.length} group(s) with ${groups.reduce((n, g) => n + 1 + g.linked.length, 0)} total paper assignment(s)?`}
                confirmButtonText="Yes, Save"
                cancelButtonText="Cancel"
                onConfirm={confirmSaveGroups}
                onClose={() => setShowSaveConfirm(false)}
            />

            {isProcessingPopupVisible && <ProcessingPopup message={processingMessage} />}
            {isErrorPopupVisible       && <ErrorPopup onClose={closeErrorPopup} message={popupErrorMessage} />}
            {isSuccessPopupVisible     && <SuccessPopup onClose={closeSuccessPopup} message={popupSuccessMessage} />}
        </div>
        </BibliographicIdsContext.Provider>
    );
};

export default UniqueStudies;